import { v4 as uuidv4 } from 'uuid';
import { db, type SyncQueueItem, type SyncOperation } from '@/shared/db/database';
import { getSyncWorker } from './syncWorker.singleton';

interface EnqueueParams {
  /** Optional stable id (e.g. draft.localId). When provided, the operation is
   *  idempotent: calling twice with the same id writes exactly ONE row (put/upsert).
   *  When omitted, a new UUID is generated and db.syncQueue.add is used. */
  id?: string;
  module: SyncQueueItem['module'];
  operation: SyncOperation;
  endpoint: string;
  payload: unknown;
}

export async function enqueueSyncOperation(params: EnqueueParams): Promise<string> {
  const id = params.id ?? uuidv4();
  const item: SyncQueueItem = {
    id,
    module: params.module,
    operation: params.operation,
    endpoint: params.endpoint,
    payload: params.payload,
    createdAt: Date.now(),
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
    status: 'pending',
  };
  if (params.id !== undefined) {
    // Idempotent upsert — same id = same row
    await db.syncQueue.put(item);
  } else {
    await db.syncQueue.add(item);
  }

  // Notify SyncWorker and trigger immediate sync when ONLINE
  const syncWorker = getSyncWorker();
  if (syncWorker) {
    // Background Sync API (Chrome/Edge only)
    void syncWorker.notifySyncWorker();
    
    // IMMEDIATE SYNC when online (all browsers)
    // Ensures offline-first operations sync instantly when connection is available
    // Without this, operations enqueued while online would wait up to 15s (polling interval)
    if (navigator.onLine) {
      void syncWorker.syncNow();
    }
  }

  return id;
}

export async function getPendingItems(module?: SyncQueueItem['module']): Promise<SyncQueueItem[]> {
  if (module) {
    return db.syncQueue
      .where('[module+status]')
      .equals([module, 'pending'])
      .sortBy('createdAt');
  }
  return db.syncQueue.where('status').equals('pending').sortBy('createdAt');
}

export async function markInProgress(id: string): Promise<void> {
  await db.syncQueue.update(id, {
    status: 'in_progress' as const,
    lastAttemptAt: Date.now(),
  });
}

export async function markSuccess(id: string): Promise<void> {
  await db.syncQueue.delete(id);
}

export async function markFailed(id: string, error: string, isPermanent: boolean): Promise<void> {
  const item = await db.syncQueue.get(id);
  if (!item) return;
  await db.syncQueue.update(id, {
    status: (isPermanent ? 'failed_permanent' : 'pending') as SyncQueueItem['status'],
    attempts: item.attempts + 1,
    lastError: error,
  });
}

export async function getQueueStats(): Promise<{ pending: number; inProgress: number; failed: number }> {
  const [pending, inProgress, failed] = await Promise.all([
    db.syncQueue.where('status').equals('pending').count(),
    db.syncQueue.where('status').equals('in_progress').count(),
    db.syncQueue.where('status').equals('failed_permanent').count(),
  ]);
  return { pending, inProgress, failed };
}
